import * as React from "react";
import { Select, Table, Tag } from "antd";
import AppModal from "../../common/components/AppModal";
import WarningBanner from "../../common/components/WarningBanner";
import type { TaskSubtaskStatusMapping } from "../../task_types/types";
import type { TaskStatus } from "../types";

type Props = {
    open: boolean;
    isLoading: boolean;
    taskStatuses: TaskStatus[];
    onCancel: () => void;
    onConfirm: (statusMappings: TaskSubtaskStatusMapping[]) => void;
};

const getStatusBadgeLabels = (status: TaskStatus) => {
    const labels: string[] = [];

    if (status.isDefault) {
        labels.push("Initial");
    }

    if (status.isFinal) {
        labels.push("Final");
    }

    return labels;
};

const RemoveSeparateSubtaskStatusesModal: React.FC<Props> = ({
    open,
    isLoading,
    taskStatuses,
    onCancel,
    onConfirm,
}) => {
    const [mappings, setMappings] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
        if (!open) {
            return;
        }

        setMappings({});
    }, [open]);

    const subtaskStatuses = React.useMemo(
        () => taskStatuses.filter((status) => status.isSubtaskStatus),
        [taskStatuses],
    );
    const mainStatuses = React.useMemo(
        () => taskStatuses.filter((status) => !status.isSubtaskStatus),
        [taskStatuses],
    );

    const statusOptions = React.useMemo(
        () =>
            mainStatuses.map((status) => ({
                label: status.name,
                value: status.id,
            })),
        [mainStatuses],
    );
    const hasSubtaskStatuses = subtaskStatuses.length > 0;
    const canConfirm =
        !isLoading &&
        mainStatuses.length > 0 &&
        (!hasSubtaskStatuses ||
            subtaskStatuses.every((status) => !!mappings[status.id]));

    const handleMappingChange = React.useCallback(
        (sourceStatusId: string, targetStatusId: string) => {
            setMappings((current) => ({
                ...current,
                [sourceStatusId]: targetStatusId,
            }));
        },
        [],
    );

    const handleConfirm = React.useCallback(() => {
        if (!canConfirm) {
            return;
        }

        onConfirm(
            subtaskStatuses.map((status) => ({
                sourceStatusId: status.id,
                targetStatusId: mappings[status.id],
            })),
        );
    }, [canConfirm, mappings, onConfirm, subtaskStatuses]);

    return (
        <AppModal
            centered
            width={680}
            open={open}
            onCancel={onCancel}
            className="alpha-modal"
            cancelButtonTitle="Cancel"
            onCancelClick={onCancel}
            cancelButtonProps={{
                disabled: isLoading,
            }}
            submitButtonTitle="Remove separate statuses"
            onSubmitClick={handleConfirm}
            submitButtonProps={{
                disabled: !canConfirm,
                loading: isLoading,
            }}
        >
            <div
                style={{
                    color: "#13254C",
                    fontSize: 18,
                    fontWeight: 700,
                }}
            >
                Remove separate subtask statuses?
            </div>
            <WarningBanner
                description={
                    hasSubtaskStatuses
                        ? "Subtasks will start using the main task statuses. Map each current subtask status to a task status before removing separate statuses."
                        : "No current subtask statuses were found. Removing separate statuses will make subtasks use the main task statuses."
                }
            />
            {hasSubtaskStatuses ? (
                <Table
                    className="alpha-table"
                    columns={[
                        {
                            title: "Current subtask status",
                            dataIndex: "name",
                            key: "name",
                            render: (name: string, status: TaskStatus) => (
                                <div
                                    style={{
                                        alignItems: "center",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 8,
                                    }}
                                >
                                    <span>{name}</span>
                                    {getStatusBadgeLabels(status).map(
                                        (label) => (
                                            <Tag
                                                key={label}
                                                style={{
                                                    background: "#EAF7EA",
                                                    border: "none",
                                                    borderRadius: 999,
                                                    color: "#21406D",
                                                    fontWeight: 600,
                                                    marginInlineEnd: 0,
                                                    paddingInline: 8,
                                                }}
                                            >
                                                {label}
                                            </Tag>
                                        ),
                                    )}
                                </div>
                            ),
                            width: "50%",
                        },
                        {
                            title: "Move subtasks to (task status)",
                            key: "targetStatus",
                            render: (_: string, status: TaskStatus) => (
                                <Select
                                    value={mappings[status.id]}
                                    onChange={(targetStatusId) =>
                                        handleMappingChange(
                                            status.id,
                                            targetStatusId,
                                        )
                                    }
                                    options={statusOptions}
                                    placeholder="Select status"
                                    style={{ width: "100%" }}
                                    optionFilterProp="label"
                                    showSearch
                                />
                            ),
                            width: "50%",
                        },
                    ]}
                    dataSource={subtaskStatuses}
                    pagination={false}
                    rowKey={(status) => status.id}
                    style={{ marginTop: 24 }}
                />
            ) : null}
        </AppModal>
    );
};

export default RemoveSeparateSubtaskStatusesModal;
